import React, { useMemo, useState } from 'react';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCopy, FaExclamationTriangle, FaPrint } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import { formatCurrency, formatDate } from '../utils';

function Linha(props) {
  return (
    <div
      className="d-flex justify-content-between align-items-start py-1"
      style={{ borderBottom: '1px solid #f0f0f0' }}
    >
      <div className="small">{props.children}</div>
      {props.direita && (
        <div className="small text-right ml-3" style={{ whiteSpace: 'nowrap' }}>
          {props.direita}
        </div>
      )}
    </div>
  );
}

function Secao(props) {
  return (
    <div className="mb-3">
      <div
        className="small font-weight-bold text-uppercase mb-1 px-1 py-1 rounded"
        style={{ background: '#e9ecef', color: '#495057', letterSpacing: '0.05em' }}
      >
        {props.titulo}
      </div>
      {props.children}
    </div>
  );
}

function chaveTexto(prefixo, texto) {
  return prefixo + '-' + String(texto || '').replace(/[^a-zA-Z0-9]+/g, '-').slice(0, 80);
}

function chaveGrupo(prefixo, g) {
  return [
    prefixo,
    g.tipo || '',
    g.valor || g.valorFuturo || '',
    g.inicio || '',
    g.fim || '',
    g.qtd || '',
  ].join('-');
}

export default function ResumoVenda(props) {
  var valorVenda = props.valorVenda || 0;
  var pagamentosSinal = props.pagamentosSinal || [];
  var condicionantes = props.condicionantes || [];
  var intermediarias = props.intermediarias || [];
  var escadinha = props.escadinha || [];
  var financiamento = props.financiamento || null;
  var condicoesEspeciais = props.condicoesEspeciais || null;
  var dataBase = props.dataBase || new Date().toISOString().substr(0, 10);
  var vencimentoFinanciamento = props.vencimentoFinanciamento || null;
  var antecipacao = props.antecipacao || null;
  var resultadoAntecipacao = props.resultadoAntecipacao || null;
  var podeExportar = props.podeExportar !== false;
  var errosExportar = props.errosExportar || [];
  var loteDescricao = props.loteDescricao || '';
  var empreendimentoNome = props.empreendimentoNome || '';
  // Valor original do lote (preço de tabela) para o cabeçalho; o valor com desconto
  // continua detalhado na seção Condições Especiais.
  var valorVendaOriginal = props.valorVendaOriginal || valorVenda;
  // Periodicidade da correção conforme a tabela ("mensal"/"anual"); vazio = genérico.
  var periodicidadeAplicacao = props.periodicidadeAplicacao || '';
  var textoDisclaimerCorrecao =
    'Todas as parcelas do financiamento/parcelamento sofrerão correção por IPCA mensal com aplicação '
    + (periodicidadeAplicacao || 'mensal ou anual')
    + ', de acordo com a tabela de preços selecionada.';

  var [gerandoProposta, setGerandoProposta] = useState(false);

  var temSinal = pagamentosSinal.length > 0;
  var temCondicionantes = condicionantes.length > 0;
  var temIntermediarias = intermediarias.length > 0;
  var temEscadinha = escadinha.length > 0;
  var temFinanciamento = financiamento && financiamento.qtd > 0;
  var temCondicoes = !!condicoesEspeciais;

  var totalSinal = pagamentosSinal.reduce(function(acc, p) {
    return acc + (p.valor || 0) * (p.qtdParcelas || 1);
  }, 0);

  // Datas estimadas do financiamento. Prioridade:
  //   1) vencimentoFinanciamento (vem do input "Vencimento 1ª parcela" — manual ou auto)
  //   2) fallback dataBase + carência CE + 1 mês (compat com chamadores antigos)
  var datasFinanciamento = useMemo(function() {
    if (!temFinanciamento) return null;
    var inicio;
    if (vencimentoFinanciamento) {
      inicio = moment(vencimentoFinanciamento);
    } else {
      var carenciaMeses = (
        condicoesEspeciais &&
        condicoesEspeciais.financiamento &&
        condicoesEspeciais.financiamento.isAtivo
      ) ? (condicoesEspeciais.financiamento.meses || 0) : 0;
      inicio = moment(dataBase).add(carenciaMeses + 1, 'months').date(20);
    }
    var fim = inicio.clone().add(financiamento.qtd - 1, 'months');
    return { inicio: inicio.toDate(), fim: fim.toDate() };
  }, [temFinanciamento, dataBase, financiamento, condicoesEspeciais, vencimentoFinanciamento]);

  // Total de VP da escadinha (já vem com valor_presente em cada parcela)
  var totalEscadinhaVP = useMemo(function() {
    return escadinha.reduce(function(acc, p) { return acc + (p.valor_presente || 0); }, 0);
  }, [escadinha]);

  // Agrupa parcelas da escadinha por série (mesmo valor consecutivo). Estrutura:
  //   [{ valor, valorPresente (1ª), qtd, inicio, fim, vpTotal }, ...]
  // Espelha a lógica de agrupamento das intermediárias para exibição compacta.
  var gruposEscadinha = useMemo(function() {
    if (!temEscadinha) return [];
    var grupos = [];
    var atual = null;
    var ordenadas = escadinha.slice().sort(function(a, b) {
      return new Date(a.vencimento) - new Date(b.vencimento);
    });
    ordenadas.forEach(function(p) {
      var vf = p.valor || 0;
      var vp = p.valor_presente || 0;
      if (atual && Math.round(atual.valor * 100) === Math.round(vf * 100)) {
        atual.qtd++;
        atual.fim = p.vencimento;
        atual.vpTotal += vp;
      } else {
        if (atual) grupos.push(atual);
        atual = {
          valor: vf,
          valorPresente: vp,
          vpTotal: vp,
          qtd: 1,
          inicio: p.vencimento,
          fim: p.vencimento,
        };
      }
    });
    if (atual) grupos.push(atual);
    return grupos;
  }, [escadinha, temEscadinha]);

  // Agrupa intermediárias por valor futuro para exibição compacta
  var gruposIntermediarias = useMemo(function() {
    if (!temIntermediarias) return [];
    var grupos = [];
    var atual = null;
    var ordenadas = intermediarias.slice().sort(function(a, b) {
      return new Date(a.vencimento) - new Date(b.vencimento);
    });
    ordenadas.forEach(function(p) {
      if (atual && Math.round(atual.valorFuturo * 100) === Math.round((p.valorFuturo || 0) * 100)) {
        atual.qtd++;
        atual.fim = p.vencimento;
        atual.vpTotal += p.valorPresente || 0;
      } else {
        if (atual) grupos.push(atual);
        atual = {
          tipo: p.tipo,
          valorFuturo: p.valorFuturo || 0,
          valorPresente: p.valorPresente || 0,
          vpTotal: p.valorPresente || 0,
          qtd: 1,
          inicio: p.vencimento,
          fim: p.vencimento,
        };
      }
    });
    if (atual) grupos.push(atual);
    return grupos;
  }, [intermediarias, temIntermediarias]);

  if (!temSinal && !temCondicionantes && !temIntermediarias && !temEscadinha && !temFinanciamento && !temCondicoes) {
    return null;
  }

  // Texto plano da simulação para "Copiar". Espelha as seções renderizadas —
  // quem alterar a UI tem que sincronizar aqui.
  function buildResumoTexto() {
    var L = [];
    L.push('Simulação Financeira');
    if (empreendimentoNome) L.push('Empreendimento: ' + empreendimentoNome);
    if (loteDescricao) L.push('Lote: ' + loteDescricao);
    L.push('Valor de venda: R$ ' + formatCurrency(valorVendaOriginal));
    L.push('');

    if (temCondicoes) {
      L.push('=== CONDIÇÕES ESPECIAIS ===');
      if (condicoesEspeciais.isIsencao) L.push('- Isenção da taxa de cessão: Sim');
      if (condicoesEspeciais.financiamento && condicoesEspeciais.financiamento.isAtivo)
        L.push('- Carência: ' + condicoesEspeciais.financiamento.meses + ' meses');
      if (condicoesEspeciais.saldoCorrecao && condicoesEspeciais.saldoCorrecao.isAtivo)
        L.push('- Saldo com correção: R$ ' + formatCurrency(condicoesEspeciais.saldoCorrecao.valor) + ' em ' + condicoesEspeciais.saldoCorrecao.data);
      if (condicoesEspeciais.valorLote && condicoesEspeciais.valorLote.isAtivo)
        L.push('- Desconto no lote: ' + formatCurrency(condicoesEspeciais.valorLote.porcentagem) + '% (R$ ' + formatCurrency(condicoesEspeciais.valorLote.valorSugerido) + ')');
      L.push('');
    }

    if (temSinal) {
      L.push('=== SINAL (R$ ' + formatCurrency(totalSinal) + ') ===');
      pagamentosSinal.forEach(function(p) {
        var qtd = p.qtdParcelas || 1;
        var total = (p.valor || 0) * qtd;
        var linha = '- ' + p.tipoLabel + ': '
          + (qtd > 1 ? qtd + 'x ' : '')
          + 'R$ ' + formatCurrency(p.valor)
          + (qtd > 1 ? ' = R$ ' + formatCurrency(total) : '')
          + ' · Vence ' + formatDate(p.vencimento);
        L.push(linha);
      });
      L.push('');
    }

    if (temCondicionantes) {
      L.push('=== PARCELAS CONDICIONANTES ===');
      condicionantes.forEach(function(c) {
        L.push('- ' + c.tipo + ': VP R$ ' + formatCurrency(c.valorPresente) + ' · ' + formatDate(c.vencimento));
      });
      L.push('');
    }

    if (temIntermediarias) {
      L.push('=== PARCELAS INTERMEDIÁRIAS (' + intermediarias.length + ') ===');
      gruposIntermediarias.forEach(function(g) {
        var prefixo = g.qtd > 1 ? g.qtd + 'x ' : '';
        var datas = g.qtd === 1 ? formatDate(g.inicio) : formatDate(g.inicio) + ' a ' + formatDate(g.fim);
        L.push('- ' + prefixo + 'VF R$ ' + formatCurrency(g.valorFuturo) + ' (VP ' + formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente) + ') · ' + datas);
      });
      L.push('');
    }

    if (temEscadinha) {
      L.push('=== PARCELAS PROGRAMADAS (' + escadinha.length + ' parcela(s) · VP Total R$ ' + formatCurrency(totalEscadinhaVP) + ') ===');
      gruposEscadinha.forEach(function(g, i) {
        var prefixo = g.qtd > 1 ? g.qtd + 'x ' : '';
        var datas = g.qtd === 1 ? formatDate(g.inicio) : formatDate(g.inicio) + ' a ' + formatDate(g.fim);
        L.push('- Série ' + (i + 1) + ': ' + prefixo + 'R$ ' + formatCurrency(g.valor) + ' (VP R$ ' + formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente) + ') · ' + datas);
      });
      L.push('');
    }

    if (temFinanciamento) {
      L.push('=== FINANCIAMENTO (' + financiamento.qtd + 'x · ' + financiamento.tipo + ') ===');
      var datasFin = datasFinanciamento
        ? formatDate(datasFinanciamento.inicio) + ' a ' + formatDate(datasFinanciamento.fim)
        : '';
      L.push('Parcela base: R$ ' + formatCurrency(financiamento.valorParcela) + ' / mês' + (datasFin ? ' · ' + datasFin : ''));
      L.push('');
    }

    // === ANTECIPAÇÃO DE PARCELAS — só aparece se houver simulação ativa (qtd ou valor)
    if (resultadoAntecipacao) {
      var r = resultadoAntecipacao;
      var direcaoLabel = r.direcao === 'inicio' ? 'do início' : 'do fim';
      L.push('=== ANTECIPAÇÃO DE PARCELAS ===');
      if (r.modo === 'valor') {
        L.push(
          'Por valor (R$ ' + formatCurrency(r.valorInformado) + ' ' + direcaoLabel + '): ' +
          r.qtdQuitadas + ' parcela(s) quitada(s), sobra R$ ' + formatCurrency(r.sobra) +
          (r.faltaProxima > 0 ? ', falta R$ ' + formatCurrency(r.faltaProxima) + ' para a próxima' : '')
        );
      } else {
        L.push('Por quantidade: ' + r.qtd + ' parcela(s) ' + direcaoLabel);
      }
      L.push('Valor original: R$ ' + formatCurrency(r.valorOriginal));
      L.push('Desconto: R$ ' + formatCurrency(r.desconto));
      L.push('Valor novo: R$ ' + formatCurrency(r.valorNovo));
      L.push('');
    }

    L.push(textoDisclaimerCorrecao);

    return L.join('\n');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Logo Setpar (cor primária #0f334f) — inlined para não depender de carregamento externo no print.
  var LOGO_SETPAR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 486.548 140.695" height="44"><path d="M-908.886,116.764c-15.179-11.16-24.43-26.14-27.13-44.8a73.966,73.966,0,0,1-.22-20.57Q-953.265,89.6-970.28,127.8a2,2,0,0,1-2.17,1.38q-16.124-.059-32.24.01a2.18,2.18,0,0,1-2.39-1.54q-18.719-42.42-37.54-84.8c-1.42-3.21-3.05-6.34-4.61-9.49a13.259,13.259,0,0,0-1.22-1.921c-2.309-3.209-7.15-4.23-11.27-4.48h-41.42V45.725h46.94v31.3h-46.97V95.734h62.349v33.311c-23.15,0-99.84.01-112.289.29-13.46.3-30.59.17-35.47-2.45-5.13-2.091-8.411-5.91-12.821-12.92-4.05-6.81-7.36-12.77-11.14-19.09a39.322,39.322,0,0,0-8.92-10.9,11.249,11.249,0,0,0-9.12-2.22v47.41H-1272V-6.356c22.26,0,44.51-.11,66.77.06,11.229.08,22.129,2.08,32.029,7.78,11.49,6.62,17.921,16.67,19.8,29.731,1.15,7.989.661,15.809-2.479,23.32-3.96,9.46-10.98,15.97-20,20.51-.481.239-.98.47-1.461.709a2.617,2.617,0,0,0-.31.3c1.12,1.43,2.32,2.81,3.35,4.31,2.4,3.469,4.36,7.29,7.11,10.49,4.96,5.78,13.79,7.89,22.69,8.221V-6.246h90.841c14.12,0,18.46-.4,23.79,1.751,6.27,2.53,10.61,6.26,14.73,14.29,3.479,6.77,6.31,13.89,9.22,20.939,5.88,14.261,11.61,28.58,17.41,42.881.179.44.4.879.74,1.63,3.05-7.411,5.99-14.56,8.93-21.721q11.97-29.129,23.9-58.289a2.218,2.218,0,0,1,2.46-1.63c12.48.049,24.97.03,37.45.03,2.42,0,3.66,1.95,2.66,4.2q-4.381,9.824-8.761,19.65a69.4,69.4,0,0,1,35.89-22.93c23.15-6.37,45.53-4.44,66.37,7.92,17.68,10.481,28.67,26.06,32.23,46.42,3.49,20.01-.65,38.4-13.139,54.61-9.491,12.28-22.04,20.19-36.821,24.66a87.844,87.844,0,0,1-24.96,3.53c-17-.181-33.159-4.511-47.329-14.931Zm47.641-88.55c-19.081.131-33.581,14.56-33.491,33.31.09,18.7,14.711,33.08,33.61,33.03,19.1-.049,33.53-14.359,33.5-33.23-.03-18.78-14.489-33.113-33.346-33.111ZM-1222.36,54.571c4.847.06,9.7.121,14.524-.2,8.311-.55,12.96-5.78,12.821-13.451-.15-7.85-5.04-13.36-12.89-13.749-5.139-.258-10.287-.225-15.438-.191-2.227.014-4.455.029-6.682.021a1.626,1.626,0,0,0-.49.16v27.36h2.359c1.926,0,3.856.026,5.8.051Z" transform="translate(1272 8.999)" fill="#fff"/></svg>';

  function buildResumoHtml() {
    var tituloHeader = 'Simulação Financeira';
    var titulo = empreendimentoNome ? tituloHeader + ' — ' + empreendimentoNome : tituloHeader;
    var dataHoraGeracao = moment().format('DD/MM/YYYY HH:mm');

    var sec = function(tit, body) {
      return ''
        + '<section class="card">'
        +   '<div class="card-head">' + escapeHtml(tit) + '</div>'
        +   '<div class="card-body">' + body + '</div>'
        + '</section>';
    };

    var rowHtml = function(esq, dir) {
      return '<div class="row"><div>' + esq + '</div><div class="right">' + (dir || '') + '</div></div>';
    };

    var planoHtml = '';
    if (temSinal) {
      planoHtml += sec('Sinal', pagamentosSinal.map(function(p) {
        var total = (p.valor || 0) * (p.qtdParcelas || 1);
        var esquerda = ''
          + (p.tipoLabel ? '<span class="pill">' + escapeHtml(p.tipoLabel) + '</span>' : '')
          + (p.qtdParcelas > 1 ? '<strong>' + p.qtdParcelas + 'x </strong>' : '')
          + '<strong>R$ ' + formatCurrency(p.valor) + '</strong>'
          + (p.qtdParcelas > 1 ? ' = R$ ' + formatCurrency(total) : '');
        return rowHtml(esquerda, escapeHtml(formatDate(p.vencimento)));
      }).join(''));
    }

    if (temCondicionantes) {
      planoHtml += sec('Parcelas Condicionantes', condicionantes.map(function(c) {
        return rowHtml(
          '<strong>' + escapeHtml(c.tipo) + '</strong>'
            + ' · VP <strong>R$ ' + formatCurrency(c.valorPresente) + '</strong>',
          escapeHtml(formatDate(c.vencimento))
        );
      }).join(''));
    }

    if (temIntermediarias) {
      planoHtml += sec('Parcelas Intermediárias', gruposIntermediarias.map(function(g) {
        var datas = g.qtd === 1
          ? formatDate(g.inicio)
          : formatDate(g.inicio) + ' → ' + formatDate(g.fim);
        return rowHtml(
          (g.qtd > 1 ? '<strong>' + g.qtd + 'x </strong>' : '')
            + 'VF <strong>R$ ' + formatCurrency(g.valorFuturo) + '</strong>'
            + ' · VP R$ ' + formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente),
          escapeHtml(datas)
        );
      }).join(''));
    }

    if (temEscadinha) {
      planoHtml += sec('Parcelas Programadas', gruposEscadinha.map(function(g, i) {
        var datas = g.qtd === 1
          ? formatDate(g.inicio)
          : formatDate(g.inicio) + ' → ' + formatDate(g.fim);
        return rowHtml(
          '<strong>Série ' + (i + 1) + '</strong>'
            + ' · ' + (g.qtd > 1 ? '<strong>' + g.qtd + 'x </strong>' : '')
            + 'VF <strong>R$ ' + formatCurrency(g.valor) + '</strong>'
            + ' · VP R$ ' + formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente),
          escapeHtml(datas)
        );
      }).join(''));
    }

    if (temFinanciamento) {
      var datasFin = datasFinanciamento
        ? formatDate(datasFinanciamento.inicio) + ' → ' + formatDate(datasFinanciamento.fim)
        : '';
      planoHtml += sec('Financiamento', rowHtml(
        '<strong>' + financiamento.qtd + 'x</strong> de <strong>R$ '
          + formatCurrency(financiamento.valorParcela) + '</strong>/mês',
        escapeHtml(datasFin)
      ));
    }

    // ---- Condições Especiais ----
    var ceHtml = '';
    if (temCondicoes) {
      var ceLinhas = [];
      if (condicoesEspeciais.isIsencao) ceLinhas.push(rowHtml('Isenção da taxa de cessão', '<strong>Sim</strong>'));
      if (condicoesEspeciais.financiamento && condicoesEspeciais.financiamento.isAtivo)
        ceLinhas.push(rowHtml('Carência do financiamento', '<strong>' + condicoesEspeciais.financiamento.meses + ' meses</strong>'));
      if (condicoesEspeciais.saldoCorrecao && condicoesEspeciais.saldoCorrecao.isAtivo)
        ceLinhas.push(rowHtml('Saldo com correção', '<strong>R$ ' + formatCurrency(condicoesEspeciais.saldoCorrecao.valor) + '</strong> · ' + escapeHtml(condicoesEspeciais.saldoCorrecao.data)));
      if (condicoesEspeciais.valorLote && condicoesEspeciais.valorLote.isAtivo)
        ceLinhas.push(rowHtml('Desconto no lote', '<strong>' + formatCurrency(condicoesEspeciais.valorLote.porcentagem) + '%</strong> → R$ <strong>' + formatCurrency(condicoesEspeciais.valorLote.valorSugerido) + '</strong>'));
      if (condicoesEspeciais.descricao)
        ceLinhas.push('<div class="row"><div class="muted" style="font-style:italic">"' + escapeHtml(condicoesEspeciais.descricao) + '"</div></div>');
      ceHtml = ceLinhas.join('');
    }

    // ---- Antecipação de Parcelas — só aparece se houver simulação ativa (qtd ou valor)
    var antecipHtml = '';
    if (resultadoAntecipacao) {
      var ra = resultadoAntecipacao;
      var direcaoLabelAnt = ra.direcao === 'inicio' ? 'do início' : 'do fim';
      var tituloAnt;
      if (ra.modo === 'valor') {
        tituloAnt = 'Simulando antecipação por valor de <strong>R$ ' + formatCurrency(ra.valorInformado) + '</strong> ' + direcaoLabelAnt + ': '
          + '<strong>' + ra.qtdQuitadas + '</strong> parcela(s) quitada(s), sobra <strong>R$ ' + formatCurrency(ra.sobra) + '</strong>'
          + (ra.faltaProxima > 0 ? ', falta <strong>R$ ' + formatCurrency(ra.faltaProxima) + '</strong> para a próxima' : '');
      } else {
        tituloAnt = 'Simulando antecipação de <strong>' + ra.qtd + '</strong> parcela(s) ' + direcaoLabelAnt + '.';
      }
      antecipHtml = ''
        + '<div class="ant-resumo">' + tituloAnt + '</div>'
        + '<div class="ant-totais">'
        +   '<div class="bloco"><span class="lbl">Valor original</span><span class="val val-danger">R$ ' + formatCurrency(ra.valorOriginal) + '</span></div>'
        +   '<div class="bloco"><span class="lbl">Desconto</span><span class="val val-info">- R$ ' + formatCurrency(ra.desconto) + '</span></div>'
        +   '<div class="bloco"><span class="lbl">Valor novo</span><span class="val val-success">R$ ' + formatCurrency(ra.valorNovo) + '</span></div>'
        + '</div>';
    }

    // ---- CSS ----
    var css = ''
      + '*{box-sizing:border-box;}'
      + 'body{font-family:"Segoe UI",Roboto,Arial,sans-serif;color:#212529;margin:0;padding:0;background:#fff;}'
      + '.wrap{max-width:900px;margin:0 auto;padding:24px;}'
      + 'header.brand{background:#0f334f;color:#fff;padding:18px 24px;display:flex;align-items:center;justify-content:space-between;gap:16px;}'
      + 'header.brand .logo{display:flex;align-items:center;gap:14px;}'
      + 'header.brand h1{font-size:18px;margin:0;font-weight:600;letter-spacing:.3px;}'
      + 'header.brand .sub{font-size:12px;opacity:.85;margin-top:2px;}'
      + 'header.brand .meta{text-align:right;font-size:11px;opacity:.9;line-height:1.5;}'
      + '.subhead{background:#f1f4f7;border-bottom:1px solid #e3e7eb;padding:10px 24px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:12px;font-size:13px;color:#3a4956;}'
      + '.subhead strong{color:#0f334f;}'
      + '.card{margin:14px 24px;border:1px solid #e3e7eb;border-radius:6px;overflow:hidden;page-break-inside:avoid;}'
      + '.card-head{background:#0f334f;color:#fff;padding:8px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;}'
      + '.card-body{padding:6px 14px;}'
      + '.row{display:flex;justify-content:space-between;align-items:flex-start;padding:6px 0;border-bottom:1px solid #f1f3f5;font-size:13px;}'
      + '.row:last-child{border-bottom:0;}'
      + '.row .right{color:#495057;white-space:nowrap;margin-left:14px;}'
      + '.muted{color:#6c757d;}'
      + '.pill{display:inline-block;background:#e9ecef;color:#495057;padding:1px 8px;border-radius:10px;font-size:11px;margin-right:6px;}'
      + '.totais{margin:18px 24px;padding:14px;background:#f8fafc;border:1px solid #dde3ea;border-radius:6px;display:flex;flex-wrap:wrap;gap:14px;justify-content:space-around;}'
      + '.totais .bloco{flex:1 1 130px;text-align:center;}'
      + '.totais .lbl{display:block;font-size:11px;color:#6c757d;text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px;}'
      + '.totais .val{display:block;font-size:15px;font-weight:600;color:#212529;}'
      + '.totais .destaque .val{color:#0f334f;font-size:17px;}'
      + '.ant-resumo{font-size:12.5px;color:#3a4956;padding:4px 0 8px;}'
      + '.ant-totais{display:flex;gap:14px;flex-wrap:wrap;padding:4px 0 2px;}'
      + '.ant-totais .bloco{flex:1 1 120px;text-align:center;padding:6px 8px;background:#f8fafc;border:1px solid #dde3ea;border-radius:5px;}'
      + '.ant-totais .lbl{display:block;font-size:10.5px;color:#6c757d;text-transform:uppercase;letter-spacing:.04em;margin-bottom:2px;}'
      + '.ant-totais .val{display:block;font-size:14px;font-weight:600;}'
      + '.ant-totais .val-danger{color:#b71c1c;}'
      + '.ant-totais .val-info{color:#0f334f;}'
      + '.ant-totais .val-success{color:#155724;}'
      + 'footer.foot{margin-top:24px;padding:12px 24px;border-top:2px solid #0f334f;font-size:10.5px;color:#6c757d;display:flex;justify-content:space-between;gap:16px;}'
      + '@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.card,header.brand,.totais{page-break-inside:avoid;}}'
      + '@page{size:A4;margin:14mm;}';

    return ''
      + '<!doctype html><html lang="pt-br"><head><meta charset="utf-8">'
      + '<title>' + escapeHtml(titulo) + '</title>'
      + '<style>' + css + '</style></head><body>'
      + '<header class="brand">'
      +   '<div class="logo">' + LOGO_SETPAR_SVG
      +     '<div>'
      +       '<h1>' + escapeHtml(tituloHeader) + '</h1>'
      +       (empreendimentoNome ? '<div class="sub">' + escapeHtml(empreendimentoNome) + '</div>' : '')
      +     '</div>'
      +   '</div>'
      +   '<div class="meta">'
      +     'Gerado em<br><strong>' + escapeHtml(dataHoraGeracao) + '</strong>'
      +   '</div>'
      + '</header>'
      + '<div class="subhead">'
      +   '<div>' + (loteDescricao ? 'Lote <strong>' + escapeHtml(loteDescricao) + '</strong>' : '') + '</div>'
      +   '<div>Valor de venda <strong>R$ ' + formatCurrency(valorVendaOriginal) + '</strong></div>'
      + '</div>'
      + planoHtml
      + (ceHtml ? sec('Condições Especiais', ceHtml) : '')
      + (antecipHtml ? sec('Antecipação de Parcelas', antecipHtml) : '')
      + '<footer class="foot">'
      +   '<div>' + escapeHtml(textoDisclaimerCorrecao) + '</div>'
      +   '<div>' + escapeHtml(dataHoraGeracao) + '</div>'
      + '</footer>'
      + '</body></html>';
  }

  function handleCopiar() {
    if (!podeExportar) return;
    var texto = buildResumoTexto();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(texto)
        .then(function() { toast.success('Simulação copiada para a área de transferência.'); })
        .catch(function() { toast.error('Não foi possível copiar.'); });
    } else {
      // Fallback antigo via textarea oculta (browsers sem clipboard API).
      var ta = document.createElement('textarea');
      ta.value = texto;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Simulação copiada.'); }
      catch (e) { toast.error('Não foi possível copiar.'); }
      document.body.removeChild(ta);
    }
  }

  function handleImprimir() {
    if (!podeExportar) return;
    var win = window.open('', '_blank', 'width=800,height=600');
    if (!win) { toast.error('Permita pop-ups para imprimir a simulação.'); return; }
    win.document.write(buildResumoHtml());
    win.document.close();
    // Pequeno timeout pra garantir render antes do print.
    setTimeout(function() {
      try { win.focus(); win.print(); } catch (e) { /* ignore */ }
    }, 250);
  }

  // A Proposta REAL (mesmo PDF do CRM, com bloco de cliente em branco) é gerada no
  // backend a partir dos dados da simulação, sem gravar a venda. O parent monta o
  // payload e chama o endpoint; aqui só disparamos.
  function handleImprimirProposta() {
    if (!podeExportar || !props.onImprimirProposta) return;
    setGerandoProposta(true);
    Promise.resolve(props.onImprimirProposta())
      .catch(function() { /* erro já tratado no parent */ })
      .then(function() { setGerandoProposta(false); });
  }

  return (
    <Card className="mb-2 border-primary">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <h6 className="mb-0 text-primary">
            <strong>Resumo Final da Simulação</strong>
          </h6>
          <div className="d-flex align-items-center">
            {!podeExportar && errosExportar.length > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="resumo-erros-export">
                    <div className="text-left small">
                      <strong>Preencha antes:</strong>
                      <ul className="mb-0 pl-3 mt-1">
                        {errosExportar.map(function(e) { return <li key={chaveTexto('erro-export', e)}>{e}</li>; })}
                      </ul>
                    </div>
                  </Tooltip>
                }
              >
                <span className="text-warning mr-2" style={{ cursor: 'help' }}>
                  <FaExclamationTriangle />
                </span>
              </OverlayTrigger>
            )}
            <Button
              variant="outline-primary"
              size="sm"
              className="mr-2"
              onClick={handleCopiar}
              disabled={!podeExportar}
              title={podeExportar ? 'Copiar a simulação' : 'Complete as informações antes de copiar'}
            >
              <FaCopy className="mr-1" />
              Copiar
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="mr-2"
              onClick={handleImprimir}
              disabled={!podeExportar}
              title={podeExportar ? 'Imprimir a simulação' : 'Complete as informações antes de imprimir'}
            >
              <FaPrint className="mr-1" />
              Imprimir
            </Button>
            {/* Gera a Proposta real (PDF do CRM) sem gravar a venda, cliente em branco. */}
            {props.onImprimirProposta && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleImprimirProposta}
                disabled={!podeExportar || gerandoProposta}
                title={podeExportar ? 'Imprimir a Proposta (cliente em branco para preenchimento)' : 'Complete as informações antes de imprimir'}
              >
                <FaPrint className="mr-1" />
                {gerandoProposta ? 'Gerando...' : 'Imprimir Proposta'}
              </Button>
            )}
          </div>
        </div>

        {/* Condições Especiais */}
        {temCondicoes && (
          <Secao titulo="Condições Especiais">
            {condicoesEspeciais.isIsencao && (
              <Linha>Isenção da taxa de cessão: <strong>Sim</strong></Linha>
            )}
            {condicoesEspeciais.financiamento && condicoesEspeciais.financiamento.isAtivo && (
              <Linha>
                Carência:{' '}
                <strong>{condicoesEspeciais.financiamento.meses} meses</strong>
                {condicoesEspeciais.financiamento.terminoLabel && (
                  <span className="text-muted"> (início aprox.: {condicoesEspeciais.financiamento.terminoLabel})</span>
                )}
              </Linha>
            )}
            {condicoesEspeciais.saldoCorrecao && condicoesEspeciais.saldoCorrecao.isAtivo && (
              <Linha>
                Saldo com correção:{' '}
                <strong>R$ {formatCurrency(condicoesEspeciais.saldoCorrecao.valor)}</strong>
                {' em '}<strong>{condicoesEspeciais.saldoCorrecao.data}</strong>
              </Linha>
            )}
            {condicoesEspeciais.valorLote && condicoesEspeciais.valorLote.isAtivo && (
              <Linha>
                Desconto no lote:{' '}
                <strong>{formatCurrency(condicoesEspeciais.valorLote.porcentagem)}%</strong>
                {' → '}
                <strong>R$ {formatCurrency(condicoesEspeciais.valorLote.valorSugerido)}</strong>
              </Linha>
            )}
            {condicoesEspeciais.descricao && (
              <Linha>
                <span className="text-muted">"{condicoesEspeciais.descricao}"</span>
              </Linha>
            )}
          </Secao>
        )}

        {/* Sinal */}
        {temSinal && (
          <Secao titulo={'Sinal — R$ ' + formatCurrency(totalSinal)}>
            {pagamentosSinal.map(function(p) {
              var total = (p.valor || 0) * (p.qtdParcelas || 1);
              return (
                <Linha
                  key={p.id}
                  direita={formatDate(p.vencimento)}
                >
                  <span className="badge badge-secondary mr-1">{p.tipoLabel}</span>
                  {p.qtdParcelas > 1 && <strong>{p.qtdParcelas}x </strong>}
                  <strong>R$ {formatCurrency(p.valor)}</strong>
                  {p.qtdParcelas > 1 && (
                    <span className="text-muted"> = R$ {formatCurrency(total)}</span>
                  )}
                  {p.emitente && <span className="text-muted ml-1">· {p.emitente}</span>}
                  {p.banco && <span className="text-muted ml-1"> Bco {p.banco} Ag {p.agencia} Nº {p.numero}</span>}
                </Linha>
              );
            })}
          </Secao>
        )}

        {/* Condicionantes */}
        {temCondicionantes && (
          <Secao titulo="Parcelas Condicionantes">
            {condicionantes.map(function(c) {
              return (
                <Linha
                  key={[
                    'cond',
                    c.codigoIntermediaria || c.tipo || '',
                    c.vencimento || '',
                    c.valorFuturo || '',
                  ].join('-')}
                  direita={formatDate(c.vencimento)}
                >
                  <strong>{c.tipo}</strong>
                  {' · '}VP <strong>R$ {formatCurrency(c.valorPresente)}</strong>
                </Linha>
              );
            })}
          </Secao>
        )}

        {/* Intermediárias */}
        {temIntermediarias && (
          <Secao titulo={'Parcelas Intermediárias — ' + intermediarias.length + ' parcela(s)'}>
            {gruposIntermediarias.map(function(g) {
              return (
                <Linha
                  key={chaveGrupo('interm', g)}
                  direita={
                    g.qtd === 1
                      ? formatDate(g.inicio)
                      : formatDate(g.inicio) + ' → ' + formatDate(g.fim)
                  }
                >
                  {g.qtd > 1 && <strong>{g.qtd}x </strong>}
                  VF <strong>R$ {formatCurrency(g.valorFuturo)}</strong>
                  {' · '}VP{' '}
                  <span className="text-muted">
                    R$ {formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente)}
                  </span>
                  {g.tipo && <span className="text-muted ml-1">({g.tipo})</span>}
                </Linha>
              );
            })}
          </Secao>
        )}

        {/* Escadinha (parcelas programadas) */}
        {temEscadinha && (
          <Secao titulo={'Parcelas Programadas — ' + escadinha.length + ' parcela(s) · VP Total R$ ' + formatCurrency(totalEscadinhaVP)}>
            {gruposEscadinha.map(function(g, i) {
              return (
                <Linha
                  key={chaveGrupo('esc', g)}
                  direita={
                    g.qtd === 1
                      ? formatDate(g.inicio)
                      : formatDate(g.inicio) + ' → ' + formatDate(g.fim)
                  }
                >
                  <strong>Série {i + 1}</strong>
                  {' · '}
                  {g.qtd > 1 && <strong>{g.qtd}x </strong>}
                  VF <strong>R$ {formatCurrency(g.valor)}</strong>
                  {' · '}VP{' '}
                  <span className="text-muted">
                    R$ {formatCurrency(g.qtd > 1 ? g.vpTotal : g.valorPresente)}
                  </span>
                </Linha>
              );
            })}
          </Secao>
        )}

        {/* Financiamento */}
        {temFinanciamento && (
          <Secao titulo={'Financiamento — ' + financiamento.qtd + 'x · ' + financiamento.tipo}>
            <Linha direita={
              datasFinanciamento
                ? formatDate(datasFinanciamento.inicio) + ' → ' + formatDate(datasFinanciamento.fim)
                : ''
            }>
              <strong>{financiamento.qtd}x</strong> de{' '}
              <strong>R$ {formatCurrency(financiamento.valorParcela)}</strong>/mês
            </Linha>
          </Secao>
        )}

        {/* Disclaimer de correção (IPCA) */}
        <div
          className="small text-muted mt-2"
          style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}
        >
          {textoDisclaimerCorrecao}
        </div>
      </Card.Body>
    </Card>
  );
}
